import BrandLogo from '../components/BrandLogo';
import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Check, CheckCircle2, Clock3, Loader2, Mail, Search } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import RegistrationAccessNotice from '../components/RegistrationAccessNotice';
import RegistrationClubNotice from '../components/RegistrationClubNotice';
import type { RegistrationAccessStatus } from '../lib/registerApi';
import TeamGenderSelect from '../components/TeamGenderSelect';
import type { Team } from '../types';
import CountrySelect from '../components/CountrySelect';
import SubtleFooter from '../components/SubtleFooter';
import { Locale, useI18n } from '../i18n';
import { getPasswordChecks, isStrongPassword } from '../lib/passwordValidation';
import {
  HockeyClub,
  completeRegistrationApi,
  getRegistrationSessionApi,
  getRegistrationClubStatusApi,
  listRegistrationClubsApi,
  startRegistrationApi,
  verifyRegistrationApi,
} from '../lib/registerApi';

type Step = 'intro' | 'identity' | 'verify' | 'club' | 'team' | 'password' | 'done';

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, locale, setLocale } = useI18n();
  const [step, setStep] = useState<Step>('intro');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [registrationId, setRegistrationId] = useState('');
  const [accessStatus, setAccessStatus] = useState<RegistrationAccessStatus | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [clubs, setClubs] = useState<HockeyClub[]>([]);
  const [selectedClub, setSelectedClub] = useState<HockeyClub | null>(null);
  const [isManualClub, setIsManualClub] = useState(false);
  const [manualClubName, setManualClubName] = useState('');
  const [manualTeamName, setManualTeamName] = useState('');
  const [teamEnvironment, setTeamEnvironment] = useState<'outdoor' | 'indoor' | 'both'>('outdoor');
  const [teamGender, setTeamGender] = useState<Team['gender']>(null);
  const [clubSearch, setClubSearch] = useState('');
  const [country, setCountry] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingClubs, setIsLoadingClubs] = useState(false);
  const [knownClub, setKnownClub] = useState<boolean | null>(null);
  const registrationClub = useMemo(() => isManualClub
    ? { name: manualClubName.trim(), is_custom: true, country }
    : selectedClub, [isManualClub, manualClubName, country, selectedClub]);

  useEffect(() => {
    let cancelled = false;
    setKnownClub(null);
    if (step === 'password' && registrationClub && registrationId) {
      getRegistrationClubStatusApi(registrationId, registrationClub)
        .then(known => { if (!cancelled) setKnownClub(known); })
        // Completion rechecks the club. An unavailable preview never blocks signup
        // or claims that a club is new; show the conditional explanation instead.
        .catch(() => { if (!cancelled) setKnownClub(null); });
    }
    return () => { cancelled = true; };
  }, [step, registrationClub, registrationId]);

  const deferredClubSearch = useDeferredValue(clubSearch);
  const passwordChecks = useMemo(() => getPasswordChecks(password), [password]);
  const hasStrongPassword = isStrongPassword(password);
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const canSubmitPasswordStep =
    !isSubmitting &&
    hasStrongPassword &&
    passwordsMatch &&
    acceptedTerms;
  const isDutchCountry = country === 'Netherlands';

  const registerSteps: Array<{ id: Exclude<Step, 'done'>; title: string; description: string }> = [
    { id: 'intro', title: t('register_step_intro_title'), description: t('register_step_intro_description') },
    { id: 'identity', title: t('register_step_identity_title'), description: t('register_step_identity_description') },
    { id: 'verify', title: t('register_step_verify_title'), description: t('register_step_verify_description') },
    { id: 'club', title: t('register_step_club_title'), description: t('register_step_club_description') },
    { id: 'team', title: t('register_step_team_title'), description: t('register_step_team_description') },
    { id: 'password', title: t('register_step_password_title'), description: t('register_step_password_description') },
  ];
  const activeStepIndex = step === 'done'
    ? registerSteps.length
    : registerSteps.findIndex((candidate) => candidate.id === step);

  const passwordCheckLabels: Record<'length' | 'upper-lower' | 'number' | 'special', string> = {
    length: t('register_password_check_length'),
    'upper-lower': t('register_password_check_upper_lower'),
    number: t('register_password_check_number'),
    special: t('register_password_check_special'),
  };

  useEffect(() => {
    let cancelled = false;
    if (step !== 'club' || !isDutchCountry || isManualClub) {
      setClubs([]);
      setIsLoadingClubs(false);
      return () => {
        cancelled = true;
      };
    }

    const normalizedQuery = deferredClubSearch.trim();
    if (normalizedQuery.length < 2) {
      setClubs([]);
      setIsLoadingClubs(false);
      return () => {
        cancelled = true;
      };
    }

    setIsLoadingClubs(true);
    listRegistrationClubsApi(normalizedQuery)
      .then((items) => {
        if (cancelled) return;
        setClubs(items);
      })
      .catch((err) => {
        if (cancelled) return;
        setClubs([]);
        setError(err instanceof Error ? err.message : t('register_error_load_clubs'));
      })
      .finally(() => {
        if (!cancelled) setIsLoadingClubs(false);
      });

    return () => {
      cancelled = true;
    };
  }, [deferredClubSearch, isDutchCountry, isManualClub, step, t]);

  const resetMessages = () => {
    setError('');
    setStatusMessage('');
  };

  const handleCountryChange = (value: string) => {
    resetMessages();
    setCountry(value);
    setClubSearch('');
    setClubs([]);
    setSelectedClub(null);
    setManualClubName('');
    setManualTeamName('');
    setIsManualClub(value !== 'Netherlands');
  };

  const handleStart = async () => {
    resetMessages();
    setIsSubmitting(true);
    try {
      const response = await startRegistrationApi(name, email, locale);
      setRegistrationId(response.registration_id);
      setEmail(response.email);
      setStatusMessage(t('register_status_code_sent', { email: response.email }));
      setStep('verify');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('register_error_start'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async () => {
    resetMessages();
    setIsSubmitting(true);
    try {
      await verifyRegistrationApi(registrationId, verificationCode);
      const session = await getRegistrationSessionApi(registrationId);
      setName(session.name);
      setEmail(session.email);
      setClubs([]);
      setSelectedClub(null);
      setCountry('');
      setIsManualClub(false);
      setManualClubName('');
      setManualTeamName('');
      setClubSearch('');
      setStatusMessage(t('register_status_email_verified'));
      setStep('club');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('register_error_verify'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFinish = async () => {
    resetMessages();
    const resolvedClub = isManualClub
      ? { name: manualClubName.trim(), is_custom: true, country }
      : selectedClub;
    const resolvedTeam = { name: manualTeamName.trim(), is_custom: true, gender: teamGender, playing_environment: teamEnvironment };

    if (!resolvedClub || !resolvedTeam || !resolvedClub.name || !resolvedTeam.name) {
      setError(t('register_error_choose_club_team'));
      return;
    }
    if (!hasStrongPassword) {
      setError(t('register_error_password_strong'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('register_error_password_mismatch'));
      return;
    }
    if (!acceptedTerms) {
      setError(t('register_error_accept_terms'));
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await completeRegistrationApi({
        registrationId,
        club: resolvedClub,
        team: resolvedTeam,
        password,
      });

      setAccessStatus({ pending: result.requires_manual_link, club_name: resolvedClub.name, team_name: resolvedTeam.name, club_managers: result.club_managers, manager_notification_status: result.manager_notification_status });
      if (result.is_first_club_manager) {
        setStatusMessage(t('register_status_first_manager'));
      } else if (result.requires_manual_link) {
        setStatusMessage(t('register_status_manual_link'));
      } else {
        setStatusMessage(t('register_status_done'));
      }
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('register_error_finish'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepBody = () => {
    switch (step) {
      case 'intro':
        return (
          <div className="space-y-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-orange-600">{t('register_intro_eyebrow')}</p>
              <h1 className="mt-2 text-3xl font-bold text-slate-900">{t('register_intro_title')}</h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                {t('register_intro_description')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setStep('identity')}
              className="flex min-h-12 w-full items-center justify-center rounded-lg bg-primary-600 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              {t('register_intro_cta')}
            </button>
            <div className="border-y border-slate-200 py-5">
              <p className="text-base font-semibold text-slate-900">{t('register_intro_features_title')}</p>
              <div className="mt-3 grid divide-y divide-slate-200 sm:grid-cols-2 sm:divide-x sm:divide-y-0">
                {[
                  t('register_intro_feature_1'),
                  t('register_intro_feature_2'),
                  t('register_intro_feature_3'),
                  t('register_intro_feature_4'),
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-3 py-3 text-sm font-medium leading-5 text-slate-700 sm:px-4 sm:first:pl-0"
                  >
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary-600" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 border-t border-slate-200 pt-4 text-sm leading-6 text-slate-600">
                {t('register_intro_terms_prefix')}{' '}
                <a
                  href="https://www.dohockey.app/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-orange-700 underline underline-offset-2 hover:text-orange-800"
                >
                  {t('register_terms_link_label')}
                </a>
                .
              </p>
            </div>
          </div>
        );
      case 'identity':
        return (
          <div className="space-y-5">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{t('register_identity_title')}</h1>
              <p className="mt-2 text-sm text-slate-600">{t('register_identity_description')}</p>
            </div>
            <label className="block space-y-2">
              <span className="text-sm font-medium text-slate-700">{t('register_identity_name')}</span>
              <input value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-orange-500" />
            </label>
            <label className="block space-y-2">
              <span className="text-sm font-medium text-slate-700">{t('register_identity_email')}</span>
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-orange-500" />
            </label>
            <div className="flex gap-3">
              <button type="button" onClick={() => setStep('intro')} className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50">{t('register_back')}</button>
              <button type="button" onClick={handleStart} disabled={isSubmitting || !name.trim() || !email.trim()} className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-primary-300 disabled:text-white/90">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                {t('register_identity_submit')}
              </button>
            </div>
          </div>
        );
      case 'verify':
        return (
          <div className="space-y-5">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{t('register_verify_title')}</h1>
              <p className="mt-2 text-sm text-slate-600">{t('register_verify_description_prefix')} <span className="font-semibold text-slate-800">{email}</span>.</p>
            </div>
            <div className="flex items-start gap-3 border-l-4 border-orange-500 bg-orange-50 px-4 py-3">
              <Mail aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-orange-700" />
              <div className="min-w-0 text-sm">
                <p className="font-semibold text-slate-900">{t('register_verify_spam_title')}</p>
                <p className="mt-1 leading-relaxed text-slate-700">{t('register_verify_spam_hint')}</p>
              </div>
            </div>
            <label className="block space-y-2">
              <span className="text-sm font-medium text-slate-700">{t('register_verify_code_label')}</span>
              <input value={verificationCode} onChange={(e) => setVerificationCode(e.target.value)} inputMode="numeric" maxLength={6} className="w-full rounded-xl border border-slate-300 px-4 py-3 tracking-[0.4em] outline-none focus:border-orange-500" />
            </label>
            <div className="flex gap-3">
              <button type="button" onClick={() => setStep('identity')} className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50">{t('register_back')}</button>
              <button type="button" onClick={handleVerify} disabled={isSubmitting || verificationCode.trim().length !== 6} className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-primary-300 disabled:text-white/90">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('register_verify_submit')}
              </button>
            </div>
          </div>
        );
      case 'club':
        return (
          <div className="space-y-5">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{t('register_club_title')}</h1>
              <p className="mt-2 text-sm text-slate-600">{t('register_club_description')}</p>
              <p className="mt-2 text-sm text-slate-600">{t('register_club_helper')}</p>
            </div>
            <div className="space-y-2">
              <label htmlFor="club-country" className="block text-sm font-medium text-slate-700">{t('register_country_label')}</label>
              <CountrySelect value={country} onChange={handleCountryChange} />
            </div>
            {!country ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                {t('register_country_helper')}
              </div>
            ) : isDutchCountry && !isManualClub ? (
              <>
                <label className="block space-y-2">
                  <span className="text-sm font-medium text-slate-700">{t('register_club_search_label')}</span>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                    <input value={clubSearch} onChange={(e) => setClubSearch(e.target.value)} className="w-full rounded-xl border border-slate-300 py-3 pl-10 pr-4 outline-none focus:border-orange-500" placeholder={t('register_club_search_placeholder')} />
                  </div>
                </label>
                <div className="max-h-72 overflow-y-auto rounded-2xl border border-slate-200">
                  {isLoadingClubs ? (
                    <div className="flex items-center gap-2 px-4 py-4 text-sm text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> {t('register_club_loading')}</div>
                  ) : clubs.length > 0 ? (
                    (selectedClub ? clubs.filter((club) => club.id === selectedClub.id) : clubs).map((club) => (
                      <button
                        key={club.id}
                        type="button"
                        onClick={() => {
                          if (selectedClub?.id === club.id) {
                            setSelectedClub(null);
                            return;
                          }
                          setSelectedClub({ ...club, country });
                          setManualTeamName('');
                        }}
                        className={`flex w-full items-center justify-between border-b border-slate-200 px-4 py-3 text-left text-sm last:border-b-0 ${selectedClub?.id === club.id ? 'bg-orange-50 text-orange-700' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                      >
                        <span className="flex min-w-0 items-center gap-3">
                          {selectedClub?.id === club.id && club.logo_url ? (
                            <img
                              src={club.logo_url}
                              alt={club.name}
                              className="h-8 w-8 rounded-full object-contain bg-white ring-1 ring-slate-200"
                              loading="lazy"
                            />
                          ) : null}
                          <span className="truncate">{club.name}</span>
                        </span>
                        {selectedClub?.id === club.id ? <CheckCircle2 className="h-4 w-4" /> : null}
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-4 text-sm text-slate-500">{t('register_club_empty')}</div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsManualClub(true);
                    setSelectedClub(null);
                    setManualTeamName('');
                  }}
                  className="text-sm font-semibold text-orange-700 hover:text-orange-800"
                >
                  {t('register_club_manual_toggle')}
                </button>
              </>
            ) : (
              <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <label className="block space-y-2">
                  <span className="text-sm font-medium text-slate-700">{t('register_club_manual_label')}</span>
                  <input
                    value={manualClubName}
                    onChange={(e) => setManualClubName(e.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-orange-500"
                    placeholder={t('register_club_manual_placeholder')}
                  />
                </label>
                {isDutchCountry ? (
                  <button
                    type="button"
                    onClick={() => {
                      setIsManualClub(false);
                      setManualClubName('');
                      setManualTeamName('');
                    }}
                    className="text-sm font-semibold text-slate-700 hover:text-slate-900"
                  >
                    {t('register_club_manual_back')}
                  </button>
                ) : null}
              </div>
            )}
            <div className="flex gap-3">
              <button type="button" onClick={() => setStep('verify')} className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50">{t('register_back')}</button>
              <button
                type="button"
                onClick={() => {
                  if (isManualClub || selectedClub) {
                    setStep('team');
                  }
                }}
                disabled={
                  !country ||
                  (isManualClub ? manualClubName.trim().length < 2 : !selectedClub)
                }
                className="flex-1 rounded-xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-primary-300 disabled:text-white/90"
              >
                {t('register_club_submit')}
              </button>
            </div>
          </div>
        );
      case 'team':
        return (
          <div className="space-y-5">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{t('register_team_title')}</h1>
              <p className="mt-2 text-sm text-slate-600">{t('register_team_description_prefix')} <span className="font-semibold text-slate-800">{isManualClub ? manualClubName : selectedClub?.name}</span></p>
              <p className="mt-2 text-sm text-slate-600">{t('register_team_helper')}</p>
            </div>
            <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <label className="block space-y-2">
                <span className="text-sm font-medium text-slate-700">{t('register_team_manual_label')}</span>
                <input
                  value={manualTeamName}
                  onChange={(e) => setManualTeamName(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-orange-500"
                  placeholder={t('register_team_manual_placeholder')}
                />
              </label>
              <TeamGenderSelect showOptionsInLabel value={teamGender} onChange={setTeamGender} unsetLabel={t('register_team_gender_undisclosed')} />
              <fieldset>
                <legend className="mb-1 text-sm font-medium text-gray-700">{t('team_environment_label')}</legend>
                <div className="grid grid-cols-3 gap-2">
                  {(['outdoor', 'indoor', 'both'] as const).map(value => <label key={value} className={`flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-lg border px-2 text-sm ${teamEnvironment === value ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-300 bg-white text-gray-700'}`}>
                    <input type="radio" name="register-team-environment" value={value} checked={teamEnvironment === value} onChange={() => setTeamEnvironment(value)} className="accent-primary-600" />
                    {t(`team_environment_choice_${value}`)}
                  </label>)}
                </div>
              </fieldset>
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setStep('club')} className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50">{t('register_back')}</button>
              <button
                type="button"
                onClick={() => {
                  if (manualTeamName.trim()) {
                    setStep('password');
                  }
                }}
                disabled={manualTeamName.trim().length < 1}
                className="flex-1 rounded-xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-primary-300 disabled:text-white/90"
              >
                {t('register_team_submit')}
              </button>
            </div>
          </div>
        );
      case 'password':
        return (
          <div className="space-y-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{t('register_password_title')}</h1>
              <p className="mt-2 text-sm text-slate-600">
                {t('register_password_description_prefix')}{' '}
                <span className="font-semibold text-slate-800">
                  {isManualClub ? manualClubName : selectedClub?.name}
                </span>{' '}
                {t('register_password_description_middle')}{' '}
                <span className="font-semibold text-slate-800">
                  {manualTeamName}
                </span>
                .
              </p>
            </div>
            <RegistrationClubNotice known={knownClub} club={registrationClub?.name || ''} team={manualTeamName} />
            <label className="block space-y-2">
              <span className="text-sm font-medium text-slate-700">{t('register_password_label')}</span>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-orange-500" />
            </label>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2">
              <p className="text-[13px] font-medium leading-5 text-slate-800">{t('register_password_requirements_title')}</p>
              <div className="mt-1 space-y-0.5">
                {passwordChecks.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 text-sm leading-[18px]">
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full border text-xs font-bold ${
                        item.valid
                          ? 'border-emerald-600 bg-emerald-600 text-white'
                          : 'border-slate-300 bg-white text-slate-400'
                      }`}
                    >
                      {item.valid ? <Check className="h-3 w-3" /> : null}
                    </span>
                    <span className={item.valid ? 'text-emerald-700' : 'text-slate-600'}>{passwordCheckLabels[item.id]}</span>
                  </div>
                ))}
              </div>
            </div>
            <label className="block space-y-2">
              <span className="text-sm font-medium text-slate-700">{t('register_password_confirm_label')}</span>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-orange-500" />
            </label>
            {confirmPassword ? (
              <p className={`text-sm ${passwordsMatch ? 'text-emerald-700' : 'text-rose-600'}`}>
                {passwordsMatch ? t('register_password_match_ok') : t('register_password_match_error')}
              </p>
            ) : null}
            <div className="rounded-2xl bg-white px-4 pb-2 pt-0">
              <label className="flex items-start gap-3 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                />
                <span>
                  {t('register_terms_checkbox_prefix')}{' '}
                  <a
                    href="https://www.dohockey.app/privacy"
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-orange-700 underline underline-offset-2 hover:text-orange-800"
                  >
                    {t('register_terms_link_label')}
                  </a>{' '}
                  {t('register_terms_checkbox_suffix')}
                </span>
              </label>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setStep('team')} className="rounded-xl border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50">{t('register_back')}</button>
              <button type="button" onClick={handleFinish} disabled={!canSubmitPasswordStep} className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-primary-300 disabled:text-white/90">
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {t('register_password_submit')}
              </button>
            </div>
          </div>
        );
      case 'done':
        return (
          <div className="space-y-6">
            <div className={`inline-flex h-14 w-14 items-center justify-center rounded-full ${accessStatus?.pending ? 'bg-slate-100 text-slate-600' : 'bg-emerald-100 text-emerald-700'}`}>
              {accessStatus?.pending ? <Clock3 className="h-7 w-7" aria-hidden="true" /> : <CheckCircle2 className="h-7 w-7" aria-hidden="true" />}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{t(accessStatus?.pending ? 'register_account_created_title' : 'register_done_title')}</h1>
              <p className="mt-3 text-sm leading-6 text-slate-600">{accessStatus?.pending ? t('register_status_manual_link') : statusMessage}</p>
            </div>
            {accessStatus && <RegistrationAccessNotice status={accessStatus} />}
            <div className="flex gap-3">
              <button type="button" onClick={() => navigate(`/${locale}/login`)} className="rounded-xl bg-primary-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600">{t('register_done_cta')}</button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#f7f8f8]">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex min-h-16 w-full max-w-6xl items-center justify-between gap-4 px-4 md:px-6">
          <BrandLogo imageClassName="h-11" />
          <div className="flex items-center gap-2 sm:gap-5">
            <Link to={`/${locale}/login`} aria-label={t('register_back_to_login')} className="inline-flex min-h-11 items-center gap-2 px-2 text-sm font-semibold text-slate-600 hover:text-primary-700">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{t('register_back_to_login')}</span>
            </Link>
            <select
              aria-label={t('language')}
              value={locale}
              onChange={(e) => setLocale(e.target.value as Locale)}
              className="min-h-10 border-0 border-b-2 border-transparent bg-transparent px-2 text-sm font-semibold text-slate-600 outline-none focus:border-primary-600"
            >
              <option value="nl">🇳🇱 NL</option>
              <option value="en">🇬🇧 EN</option>
            </select>
          </div>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-6xl flex-grow items-start px-0 py-0 md:px-6 md:py-10">
        <div className="grid w-full border-y border-slate-200 bg-white lg:grid-cols-[minmax(0,1.12fr)_minmax(320px,0.88fr)] lg:divide-x lg:divide-slate-200">
          <section className="order-2 p-6 sm:p-8 lg:order-1 lg:p-12">
            {error ? (
              <div className="mb-5 border-l-2 border-rose-500 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}
            {renderStepBody()}
          </section>

          <aside className="order-1 border-b border-slate-200 bg-slate-50 p-6 sm:p-8 lg:order-2 lg:border-b-0 lg:p-10">
            <div className="lg:sticky lg:top-8">
              <p className="ui-kicker">{t('register_sidebar_eyebrow')}</p>
              <h2 className="ui-section-title mt-2">{t('register_sidebar_title')}</h2>
              <p className="mt-3 hidden text-sm leading-6 text-slate-600 lg:block">
                {t('register_sidebar_description')}
              </p>
              <div className="mt-5 lg:hidden">
                <div className="flex items-center justify-between gap-3 text-xs font-semibold text-slate-500">
                  <span>{activeStepIndex >= registerSteps.length ? registerSteps.length : activeStepIndex + 1} / {registerSteps.length}</span>
                  <span className="text-right text-slate-800">{step === 'done' ? t('register_done_title') : registerSteps[Math.max(activeStepIndex, 0)]?.title}</span>
                </div>
                <div className="mt-3 grid grid-cols-6 gap-1.5" aria-hidden="true">
                  {registerSteps.map((item, index) => <span key={item.id} className={`h-1.5 ${activeStepIndex >= index ? 'bg-primary-600' : 'bg-slate-200'}`} />)}
                </div>
              </div>
              <div className="relative mt-6 hidden lg:block">
                <div className="absolute bottom-3 left-[0.95rem] top-3 w-px bg-slate-300" />
                <div className="space-y-2">
                  {registerSteps.map((item, index) => {
                  const isActive = step === item.id;
                  const isComplete = activeStepIndex > index;

                  return (
                    <div
                      key={item.id}
                      className="relative pl-11"
                    >
                      <div
                        className={`absolute left-0 top-3 flex h-8 w-8 items-center justify-center rounded-full border text-sm font-bold ${
                          isActive
                            ? 'border-primary-600 bg-primary-600 text-white'
                            : isComplete
                              ? 'border-primary-200 bg-primary-50 text-primary-700'
                              : 'border-slate-300 bg-white text-slate-500'
                        }`}
                      >
                        {isComplete ? <Check className="h-4 w-4" /> : index + 1}
                      </div>
                      <div className={`border-b border-slate-200 py-2.5 ${isActive ? 'text-slate-950' : 'text-slate-600'}`}>
                        <p className="text-sm font-bold leading-5">{item.title}</p>
                        <p className="mt-0.5 text-xs leading-5 text-slate-500">{item.description}</p>
                      </div>
                    </div>
                  );
                  })}
                </div>
              </div>
            </div>
          </aside>
        </div>

      </main>
      <SubtleFooter />
    </div>
  );
};

export default RegisterPage;
